import { z } from "zod";
import { paginationQuery, searchParam } from "../pagination";
import { MARK_DECIMALS, MARK_LIMIT, formatMark, parseMark } from "./marks";
import type { ClusterRef } from "../transport";
import {
	clientIdField,
	competitionCodeField,
	enCompetitionFieldMessages,
	makeCompetitionCodeField,
	type NameTranslations,
	nameTranslationsField,
	TRANSLATION_LOCALES,
	type TranslationLocale,
} from "./common";
import {
	COMPETITOR_TYPES,
	type CompetitorType,
	EVENT_GENDERS,
	type EventGender,
	RESULT_FAMILIES,
	RESULT_TYPES,
	type ResultFamily,
	type ResultType,
	SIDE_SCHEMES,
	type SideScheme,
} from "./vocab";

/**
 * `/games`, `/sports`, `/disciplines`, `/events`, `/sport-classes`, `/venues` —
 * the setup tree every other competition record hangs from. The GMS settings
 * pages write it; OVR's navigation and every picker read it.
 *
 *   games → sports → disciplines → events      (each level has a `code`)
 *                    sport_classes             (para classes, owned by a sport)
 *   venues → courts / mats / pools             (one level of nesting, no games)
 *
 * Wire shapes only (snake_case). The API's schemas wrap the request schemas
 * below and only map them to camelCase, so the web and the server cannot
 * disagree about what a valid body is.
 *
 * Every list is paginated and ordered by `(sort_order, code)` unless told
 * otherwise; `code` breaks ties on every sort. Tables without a `sort_order`
 * (games, sport classes, venues) order by `code`. The `/options` lists return
 * the whole set: reference data a picker needs in one piece, measured in tens
 * (at most a few hundred) of rows.
 */

// ─── Field rules ─────────────────────────────────────────────────────────────

/** `name` on every reference row. Trimmed FIRST — zod 3 runs checks in order, so `.min(1).trim()` would let "   " through. */
export const REFERENCE_NAME_MAX = 200;
/**
 * The rule builders below take OPTIONAL messages: the wire schemas call them
 * with none (zod's own English, as the API has always answered), the GMS form
 * factories at the bottom of this file with translated ones. One builder, so
 * the form and the API cannot drift. `.min(1, undefined)` is exactly `.min(1)`.
 */
const nameRule = (required?: string, tooLong?: string) => z.string().trim().min(1, required).max(REFERENCE_NAME_MAX, tooLong);
export const referenceNameField = nameRule();

/** A whole number in `min..max`; one message for every way it can be wrong. */
const intRule = (min: number, max: number, message?: string) =>
	z
		.number(message === undefined ? undefined : { required_error: message, invalid_type_error: message })
		.int(message)
		.min(min, message)
		.max(max, message);

/** `sort_order`: the programme is not alphabetical. */
export const SORT_ORDER_MAX = 9999;
export const sortOrderField = intRule(0, SORT_ORDER_MAX);

/**
 * The audit headline a PATCH may carry. The same rule as the API's own
 * `reasonField` (`apps/api/src/schemas/common.schema.ts`), which the DELETE
 * routes use; `contract-parity.reference.test.ts` holds the two to one verdict.
 */
export const REASON_MAX = 300;
const reasonField = z.string().trim().min(1).max(REASON_MAX);

const idField = z.string().uuid();
const dirParam = z.enum(["asc", "desc"]);

/** A boolean in a query string. Never `z.coerce.boolean()`: `Boolean("false")` is true. */
const booleanParam = z.enum(["true", "false"]);

/**
 * An address the browser will fetch — `https://…` or `http://…` only. `.url()`
 * alone also passes `javascript:` and `data:` URLs.
 */
export const HTTP_URL_MAX = 2048;
const httpUrlRule = (message?: string) =>
	z
		.string()
		.trim()
		.url(message)
		.max(HTTP_URL_MAX, message)
		.refine((v) => /^https?:\/\//i.test(v), message ?? "Must start with http:// or https://");
const httpUrlField = httpUrlRule();

/** A PATCH must change something; `reason` alone is not a change. */
const changesSomething = (body: Record<string, unknown>): boolean =>
	Object.entries(body).some(([key, value]) => key !== "reason" && value !== undefined);
const NOTHING_TO_CHANGE = "At least one field must be provided";

// ─── Games ───────────────────────────────────────────────────────────────────

export const GAMES_SORT_KEYS = ["code", "name", "starts_on", "created_at"] as const;
export type GamesSortKey = (typeof GAMES_SORT_KEYS)[number];

export const listGamesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	sort: z.enum(GAMES_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListGamesQuery = z.input<typeof listGamesQuery>;

/** An IANA zone name — `Asia/Kuala_Lumpur`. Whether Postgres knows it is decided by the UPDATE itself (422 `{ field: "timezone" }`). */
export const TIMEZONE_MAX = 64;
const timezoneField = z.string().trim().min(1).max(TIMEZONE_MAX);

/** A calendar date in the games' own timezone, `YYYY-MM-DD`. */
const calendarDateField = z.string().trim().date();

/**
 * `PATCH /games/:id` (SUPER_ADMIN). `code` and `is_para` are not editable: the
 * code is what other systems key on, and a games does not change which kind it
 * is. `null` clears a date. A start after the end is refused — here when both
 * are sent, by the UPDATE against the stored date otherwise.
 */
export const updateGamesBody = z
	.object({
		name: referenceNameField.optional(),
		timezone: timezoneField.optional(),
		starts_on: calendarDateField.nullable().optional(),
		ends_on: calendarDateField.nullable().optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE)
	.refine((b) => !b.starts_on || !b.ends_on || b.starts_on <= b.ends_on, {
		message: "The games cannot end before it starts",
		path: ["ends_on"],
	});
export type UpdateGamesBody = z.input<typeof updateGamesBody>;

/** One row of `GET /games`, and `GET /games/:id`. */
export interface GamesItem {
	games_id: string;
	code: string;
	name: string;
	is_para: boolean;
	/** IANA name. Decides which DAY a meet or a medal falls on — never the server's zone, never the viewer's. */
	timezone: string;
	/** `YYYY-MM-DD` in `timezone`; null until the organisers confirm. */
	starts_on: string | null;
	ends_on: string | null;
	sports_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /games/options` — both games, the main games first. */
export interface GamesOption {
	games_id: string;
	code: string;
	name: string;
	is_para: boolean;
}

// ─── Sports ──────────────────────────────────────────────────────────────────

export const SPORT_SORT_KEYS = ["sort_order", "code", "name", "created_at"] as const;
export type SportSortKey = (typeof SPORT_SORT_KEYS)[number];

export const listSportsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	games_id: idField.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(SPORT_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListSportsQuery = z.input<typeof listSportsQuery>;

/** `GET /sports/options` and `GET /sports/tree`: one games at a time. */
export const sportOptionsQuery = z.object({ games_id: idField });
export const sportTreeQuery = z.object({ games_id: idField });

export const createSportBody = z.object({
	sport_id: clientIdField,
	games_id: idField,
	code: competitionCodeField,
	name: referenceNameField,
	name_translations: nameTranslationsField.optional(),
	icon_url: httpUrlField.optional(),
	sort_order: sortOrderField.optional(),
	is_active: z.boolean().optional(),
});
export type CreateSportBody = z.input<typeof createSportBody>;

/** `games_id` is not editable: a sport's events, classes and cards belong to that games. `null` clears `icon_url`. */
export const updateSportBody = z
	.object({
		code: competitionCodeField.optional(),
		name: referenceNameField.optional(),
		name_translations: nameTranslationsField.optional(),
		icon_url: httpUrlField.nullable().optional(),
		sort_order: sortOrderField.optional(),
		is_active: z.boolean().optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE);
export type UpdateSportBody = z.input<typeof updateSportBody>;

/** One row of `GET /sports`; also `GET /sports/:id` and what a create or edit returns. */
export interface SportItem {
	sport_id: string;
	games_id: string;
	games_code: string;
	code: string;
	name: string;
	name_translations: NameTranslations;
	icon_url: string | null;
	sort_order: number;
	/** Hidden from pickers and the tree when false — never deleted, results may hang off it. */
	is_active: boolean;
	disciplines_count: number;
	/** Every event under the sport's disciplines. */
	events_count: number;
	created_at: string;
	updated_at: string;
}

/** `GET /sports/options?games_id=` — active sports of one games. */
export interface SportOption {
	sport_id: string;
	code: string;
	name: string;
}

export interface SportTreeDiscipline {
	discipline_id: string;
	code: string;
	name: string;
	/** Active events only — the ones the navigation will show. */
	events_count: number;
}

/** One sport of `GET /sports/tree?games_id=`: active sports, each with its active disciplines, both by `(sort_order, code)`. */
export interface SportTreeNode {
	sport_id: string;
	code: string;
	name: string;
	disciplines: SportTreeDiscipline[];
}

/**
 * `GET /sports/stats?games_id=` — the sports page's strip, one games at a
 * time (the games is the page's view; search and status do not narrow it).
 */
export const sportStatsQuery = z.object({ games_id: idField });
export type SportStatsQuery = z.input<typeof sportStatsQuery>;

export interface SportStats {
	sports_total: number;
	sports_active: number;
	sports_inactive: number;
	disciplines_total: number;
	disciplines_active: number;
	events_total: number;
	events_active: number;
}

// ─── Disciplines ─────────────────────────────────────────────────────────────

/** `sport` orders by the parent sport first (its `sort_order`, `code`), then the discipline's own. */
export const DISCIPLINE_SORT_KEYS = ["sort_order", "code", "name", "sport", "created_at"] as const;
export type DisciplineSortKey = (typeof DISCIPLINE_SORT_KEYS)[number];

export const listDisciplinesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	games_id: idField.optional(),
	sport_id: idField.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(DISCIPLINE_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListDisciplinesQuery = z.input<typeof listDisciplinesQuery>;

/** `GET /disciplines/options` — one sport's, or a whole games'. One of the two is required: never "every discipline of every games". */
export const disciplineOptionsQuery = z
	.object({ sport_id: idField.optional(), games_id: idField.optional() })
	.refine((q) => q.sport_id !== undefined || q.games_id !== undefined, {
		message: "Give sport_id or games_id",
		path: ["sport_id"],
	});
export type DisciplineOptionsQuery = z.input<typeof disciplineOptionsQuery>;

export const createDisciplineBody = z.object({
	discipline_id: clientIdField,
	sport_id: idField,
	code: competitionCodeField,
	name: referenceNameField,
	name_translations: nameTranslationsField.optional(),
	/** Omitted = SEQUENCE (no named sides, the start order decides). */
	side_scheme: z.enum(SIDE_SCHEMES).optional(),
	sort_order: sortOrderField.optional(),
	is_active: z.boolean().optional(),
});
export type CreateDisciplineBody = z.input<typeof createDisciplineBody>;

/** `sport_id` is not editable: a discipline moved under another sport would take its events into another games' programme. */
export const updateDisciplineBody = z
	.object({
		code: competitionCodeField.optional(),
		name: referenceNameField.optional(),
		name_translations: nameTranslationsField.optional(),
		side_scheme: z.enum(SIDE_SCHEMES).optional(),
		sort_order: sortOrderField.optional(),
		is_active: z.boolean().optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE);
export type UpdateDisciplineBody = z.input<typeof updateDisciplineBody>;

export interface DisciplineItem {
	discipline_id: string;
	sport_id: string;
	sport_code: string;
	sport_name: string;
	games_id: string;
	code: string;
	name: string;
	name_translations: NameTranslations;
	/** What the two sides of a head-to-head are called (`SIDE_SCHEMES`). */
	side_scheme: SideScheme;
	sort_order: number;
	is_active: boolean;
	events_count: number;
	created_at: string;
	updated_at: string;
}

/** Active disciplines, ordered by sport, then by discipline. */
export interface DisciplineOption {
	discipline_id: string;
	sport_id: string;
	code: string;
	name: string;
	/**
	 * Top-level meets of the discipline's events, scheduled or not — what
	 * `GET /meets/status-counts?discipline_id=` adds up to (a tab's "Swimming 164").
	 */
	meets_count: number;
}

/** `GET /disciplines/stats?sport_id=` — one sport's disciplines (the drill-down's strip). */
export const disciplineStatsQuery = z.object({ sport_id: idField });
export type DisciplineStatsQuery = z.input<typeof disciplineStatsQuery>;

export interface DisciplineStats {
	total: number;
	active: number;
	inactive: number;
	/** Events under the sport's disciplines, active or not. */
	events_total: number;
}

// ─── Events ──────────────────────────────────────────────────────────────────

/**
 * `discipline` orders by sport, then discipline, then the event's own
 * `(sort_order, code)` — the programme order (OVR's). `event` orders by
 * sport, then discipline, then `compareEvents` (gender by gender, each
 * event shortest first) — the GMS default.
 */
export const EVENT_SORT_KEYS = ["sort_order", "code", "name", "gender", "discipline", "event", "created_at"] as const;
export type EventSortKey = (typeof EVENT_SORT_KEYS)[number];

export const listEventsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	games_id: idField.optional(),
	sport_id: idField.optional(),
	discipline_id: idField.optional(),
	gender: z.enum(EVENT_GENDERS).optional(),
	competitor_type: z.enum(COMPETITOR_TYPES).optional(),
	is_medal_event: booleanParam.optional(),
	is_active: booleanParam.optional(),
	sort: z.enum(EVENT_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListEventsQuery = z.input<typeof listEventsQuery>;

export const eventOptionsQuery = z.object({ discipline_id: idField });

/**
 * Roster bounds of a TEAM event: 2–2 for a doubles pair, 4–6 for a relay
 * squad, up to a football squad. 60 is also the most members one competitor
 * can be given in one call, so a larger bound could never be filled.
 */
export const TEAM_SIZE_LIMIT = 60;
/** Expected medals of one colour: 2 bronzes in most combat sports; 4 covers a tie. The table's CHECK says the same. */
export const MEDAL_COUNT_MAX = 4;
/** The entry quota per organization. Keeps the value inside the `smallint` column, far above any real quota. */
export const MAX_ENTRIES_PER_NOC_LIMIT = 99;
/** Classes one event may be open to — "100m T11–T13" is three. */
export const EVENT_SPORT_CLASSES_MAX = 30;
/** The shortlist quota per organization (0062, `max_shortlist_per_noc`): 1..99, like the entry quota. */
export const MAX_SHORTLIST_PER_NOC_LIMIT = 99;
/** A qualifying standard's note ("Achieved 2026-01-01 or later at a sanctioned meet"). */
export const QUALIFYING_NOTE_MAX = 200;

/**
 * An event's qualifying standard (0062): `mark` in the canonical unit of
 * `result_type` (`parseMark` / `formatMark`, `marks.ts`), met when the
 * athlete's best record is at or past it in the `lower_is_better` direction
 * (`meetsStandard`).
 */
export interface EventQualifyingStandard {
	mark: number;
	note: string | null;
	lower_is_better: boolean;
	result_type: ResultType;
}

const teamSizeRule = (message?: string) => intRule(1, TEAM_SIZE_LIMIT, message);
const medalCountRule = (message?: string) => intRule(0, MEDAL_COUNT_MAX, message);
const maxEntriesRule = (message?: string) => intRule(1, MAX_ENTRIES_PER_NOC_LIMIT, message);
const maxShortlistRule = (message?: string) => intRule(1, MAX_SHORTLIST_PER_NOC_LIMIT, message);
const teamSizeField = teamSizeRule();
const medalCountField = medalCountRule();
const maxEntriesField = maxEntriesRule();
const maxShortlistField = maxShortlistRule();
/** A canonical mark: ≥ 0, below MARK_LIMIT, ≤ MARK_DECIMALS decimals (`numeric(14,3)`). */
const markField = z
	.number()
	.min(0)
	.lt(MARK_LIMIT)
	.refine((v) => Number(v.toFixed(MARK_DECIMALS)) === v, `At most ${MARK_DECIMALS} decimals`);
const qualifyingNoteField = z.string().trim().min(1).max(QUALIFYING_NOTE_MAX);

const SHORTLIST_BELOW_ENTRIES = "The shortlist quota cannot be below the entry quota";
const MARK_NEEDS_MEASURE = "An event whose result measures nothing has no qualifying mark";

/** The two quota / standard rules of the `events` CHECKs a body can break on its own (0062). */
function checkQuotaAndStandard(
	b: { competitor_type?: string; max_entries_per_noc?: number | null; max_shortlist_per_noc?: number | null; result_type?: string; qualifying_mark?: number | null },
	ctx: z.RefinementCtx,
	belowEntries: string,
	needsMeasure: string,
): void {
	if (b.competitor_type === "INDIVIDUAL" && b.max_entries_per_noc != null && b.max_shortlist_per_noc != null && b.max_shortlist_per_noc < b.max_entries_per_noc) {
		ctx.addIssue({ code: "custom", path: ["max_shortlist_per_noc"], message: belowEntries });
	}
	if (b.result_type === "NONE" && b.qualifying_mark != null) ctx.addIssue({ code: "custom", path: ["qualifying_mark"], message: needsMeasure });
}
/** An instant with its offset — `2027-12-05T19:30:00+07:00` or `…Z`. Stored as `timestamptz`. */
const instantField = z.string().trim().datetime({ offset: true });

/**
 * Classes of the event's OWN sport (S9 means nothing outside swimming); any
 * other is refused with 409 `{ field: "sport_class_ids" }`. Lower-cased so the
 * duplicate check sees "AB…" and "ab…" as the one uuid Postgres would.
 */
const sportClassIdsRule = (tooMany?: string, duplicate?: string) =>
	z
		.array(z.string().uuid().toLowerCase())
		.max(EVENT_SPORT_CLASSES_MAX, tooMany)
		.refine((ids) => new Set(ids).size === ids.length, duplicate ?? "List each class once");
const sportClassIdsField = sportClassIdsRule();

const TEAM_ONLY = "Team sizes apply only to TEAM events";
const MIN_OVER_MAX = "The maximum cannot be below the minimum";

/** The two team-size rules of the `events` CHECK constraints, for a create body and the GMS form alike. */
function checkTeamSizes(
	b: { competitor_type?: string; team_size_min?: number | null; team_size_max?: number | null },
	ctx: z.RefinementCtx,
	teamOnly: string,
	minOverMax: string,
): void {
	if (b.competitor_type !== "TEAM") {
		if (b.team_size_min != null) ctx.addIssue({ code: "custom", path: ["team_size_min"], message: teamOnly });
		if (b.team_size_max != null) ctx.addIssue({ code: "custom", path: ["team_size_max"], message: teamOnly });
	}
	if (b.team_size_min != null && b.team_size_max != null && b.team_size_min > b.team_size_max) {
		ctx.addIssue({ code: "custom", path: ["team_size_max"], message: minOverMax });
	}
}

/**
 * `POST /events`. The rules of the table's CHECK constraints are checked here,
 * so a bad body is a 422 that names the field rather than a database error:
 * team sizes only on a TEAM event, `team_size_min <= team_size_max`, medal
 * counts 0–4, `max_entries_per_noc >= 1`.
 */
export const createEventBody = z
	.object({
		event_id: clientIdField,
		discipline_id: idField,
		code: competitionCodeField,
		name: referenceNameField,
		name_translations: nameTranslationsField.optional(),
		gender: z.enum(EVENT_GENDERS),
		competitor_type: z.enum(COMPETITOR_TYPES),
		team_size_min: teamSizeField.optional(),
		team_size_max: teamSizeField.optional(),
		result_family: z.enum(RESULT_FAMILIES).optional(),
		result_type: z.enum(RESULT_TYPES).optional(),
		lower_is_better: z.boolean().optional(),
		is_medal_event: z.boolean().optional(),
		gold_count: medalCountField.optional(),
		silver_count: medalCountField.optional(),
		bronze_count: medalCountField.optional(),
		medal_ceremony_at: instantField.optional(),
		max_entries_per_noc: maxEntriesField.optional(),
		max_shortlist_per_noc: maxShortlistField.optional(),
		qualifying_mark: markField.optional(),
		qualifying_note: qualifyingNoteField.optional(),
		sort_order: sortOrderField.optional(),
		is_active: z.boolean().optional(),
		sport_class_ids: sportClassIdsField.optional(),
	})
	// Neither size is nullable on a create, so `!= null` in the shared check is `!== undefined` here.
	.superRefine((b, ctx) => {
		checkTeamSizes(b, ctx, TEAM_ONLY, MIN_OVER_MAX);
		// An omitted result type is the table's default, NONE.
		checkQuotaAndStandard({ ...b, result_type: b.result_type ?? "NONE" }, ctx, SHORTLIST_BELOW_ENTRIES, MARK_NEEDS_MEASURE);
	});
export type CreateEventBody = z.input<typeof createEventBody>;

/**
 * `PATCH /events/:id` — everything a create takes except `discipline_id`.
 * `null` clears a team size, the ceremony time or the entry quota.
 *
 * Decided by the server, against what is stored:
 *  - `competitor_type` cannot change once the event has competitors (409 `{ field: "competitor_type" }`);
 *    changing it to INDIVIDUAL clears both team sizes, which an individual event cannot have.
 *  - `result_family`, `result_type` and `lower_is_better` cannot change while
 *    a meet that takes that setting from the event (its own override is null)
 *    holds results — a saved result, a slot or line-up `detail`, or a meet
 *    `config` / `detail`: 409 `{ field: "result_family" | "result_type" |
 *    "lower_is_better", in_use: "results" }`, naming the first such field. A
 *    meet with its own override is not affected and does not block. Sending
 *    the stored value is not a change.
 *  - `sport_class_ids` REPLACES the set; a class a competitor member still
 *    competes in cannot be removed (409 `{ field: "sport_class_ids" }`).
 *  - A LOCKED event takes only `name`, `name_translations`, `sort_order` and
 *    `medal_ceremony_at` (anything else: 409 `{ locked: true }`).
 *  - A team size the stored type or bounds do not allow is a 422 naming it.
 *  - (0062) `max_shortlist_per_noc` below the stored entry quota of an
 *    INDIVIDUAL event, or a `qualifying_mark` on an event whose stored
 *    `result_type` is NONE, is a 422 naming the field. Changing `result_type`
 *    without sending `qualifying_mark` clears the stored mark (its unit
 *    changed). Quotas and the standard are not result settings: results do
 *    not freeze them.
 */
export const updateEventBody = z
	.object({
		code: competitionCodeField.optional(),
		name: referenceNameField.optional(),
		name_translations: nameTranslationsField.optional(),
		gender: z.enum(EVENT_GENDERS).optional(),
		competitor_type: z.enum(COMPETITOR_TYPES).optional(),
		team_size_min: teamSizeField.nullable().optional(),
		team_size_max: teamSizeField.nullable().optional(),
		result_family: z.enum(RESULT_FAMILIES).optional(),
		result_type: z.enum(RESULT_TYPES).optional(),
		lower_is_better: z.boolean().optional(),
		is_medal_event: z.boolean().optional(),
		gold_count: medalCountField.optional(),
		silver_count: medalCountField.optional(),
		bronze_count: medalCountField.optional(),
		medal_ceremony_at: instantField.nullable().optional(),
		max_entries_per_noc: maxEntriesField.nullable().optional(),
		max_shortlist_per_noc: maxShortlistField.nullable().optional(),
		qualifying_mark: markField.nullable().optional(),
		qualifying_note: qualifyingNoteField.nullable().optional(),
		sort_order: sortOrderField.optional(),
		is_active: z.boolean().optional(),
		sport_class_ids: sportClassIdsField.optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE)
	.superRefine((b, ctx) => {
		if (b.competitor_type === "INDIVIDUAL") {
			if (b.team_size_min != null) ctx.addIssue({ code: "custom", path: ["team_size_min"], message: TEAM_ONLY });
			if (b.team_size_max != null) ctx.addIssue({ code: "custom", path: ["team_size_max"], message: TEAM_ONLY });
		}
		if (b.team_size_min != null && b.team_size_max != null && b.team_size_min > b.team_size_max) {
			ctx.addIssue({ code: "custom", path: ["team_size_max"], message: MIN_OVER_MAX });
		}
		checkQuotaAndStandard(b, ctx, SHORTLIST_BELOW_ENTRIES, MARK_NEEDS_MEASURE);
	});
export type UpdateEventBody = z.input<typeof updateEventBody>;

/** One row of `GET /events`. */
export interface EventItem {
	event_id: string;
	discipline_id: string;
	discipline_code: string;
	discipline_name: string;
	sport_id: string;
	sport_code: string;
	sport_name: string;
	games_id: string;
	code: string;
	name: string;
	name_translations: NameTranslations;
	gender: EventGender;
	competitor_type: CompetitorType;
	/** TEAM events only. */
	team_size_min: number | null;
	team_size_max: number | null;
	/**
	 * Defaults for the event's meets; a meet may override all three. Frozen,
	 * one by one, once a meet that inherits it holds results.
	 */
	result_family: ResultFamily;
	result_type: ResultType;
	lower_is_better: boolean;
	is_medal_event: boolean;
	/** What the event is EXPECTED to award — a warning for the medal desk, never a medal. */
	gold_count: number;
	silver_count: number;
	bronze_count: number;
	medal_ceremony_at: string | null;
	/** Entry quota per organization (entries that compete: the EBN and medal cap); null = no limit. */
	max_entries_per_noc: number | null;
	/** Shortlist quota per organization (PRIMARY athletes, `shortlistPrimaryLimit`); null = falls back. */
	max_shortlist_per_noc: number | null;
	/** Qualifying standard in the canonical unit of `result_type` (`formatMark`); null = none. */
	qualifying_mark: number | null;
	qualifying_note: string | null;
	/** Set once the medals are awarded: results and most settings are frozen until it is unlocked. */
	locked_at: string | null;
	sort_order: number;
	is_active: boolean;
	phases_count: number;
	meets_count: number;
	competitors_count: number;
	created_at: string;
	updated_at: string;
}

/** A class an event is open to. */
export interface EventSportClassRef {
	sport_class_id: string;
	code: string;
	name: string | null;
}

/** `GET /events/:id`, and what a create or edit returns: the item plus its classes, by `code`. */
export interface EventDetail extends EventItem {
	sport_classes: EventSportClassRef[];
}

/** `GET /events/options?discipline_id=` — active events of one discipline. */
export interface EventOption {
	event_id: string;
	code: string;
	name: string;
	gender: EventGender;
	competitor_type: CompetitorType;
}

/**
 * `GET /events/stats?games_id=&sport_id=&discipline_id=` — the events page's
 * strip and the gender / competitor-type chip counts. Narrowed by the
 * games → sport → discipline cascade only: the chips it counts are the
 * other filters, so they are not applied to their own counts.
 */
export const eventStatsQuery = z.object({
	games_id: idField,
	sport_id: idField.optional(),
	discipline_id: idField.optional(),
});
export type EventStatsQuery = z.input<typeof eventStatsQuery>;

export interface EventStats {
	total: number;
	/** Every gender in `EVENT_GENDERS` order, zero counts included. */
	by_gender: Array<{ gender: EventGender; count: number }>;
	/** Every type in `COMPETITOR_TYPES` order, zero counts included. */
	by_competitor_type: Array<{ competitor_type: CompetitorType; count: number }>;
}

// ─── Sport classes ───────────────────────────────────────────────────────────

export const SPORT_CLASS_SORT_KEYS = ["code", "name", "sport"] as const;
export type SportClassSortKey = (typeof SPORT_CLASS_SORT_KEYS)[number];

export const listSportClassesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	sport_id: idField.optional(),
	sort: z.enum(SPORT_CLASS_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListSportClassesQuery = z.input<typeof listSportClassesQuery>;

export const sportClassOptionsQuery = z.object({ sport_id: idField });

/** `code` follows the competition code rule — S9, T11, BC3, SB8 — stored upper-case. `name` is optional. */
export const createSportClassBody = z.object({
	sport_class_id: clientIdField,
	sport_id: idField,
	code: competitionCodeField,
	name: referenceNameField.optional(),
});
export type CreateSportClassBody = z.input<typeof createSportClassBody>;

/** `sport_id` is not editable: events and athletes use the class as a class OF that sport. `null` clears `name`. */
export const updateSportClassBody = z
	.object({
		code: competitionCodeField.optional(),
		name: referenceNameField.nullable().optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE);
export type UpdateSportClassBody = z.input<typeof updateSportClassBody>;

export interface SportClassItem {
	sport_class_id: string;
	sport_id: string;
	sport_code: string;
	code: string;
	name: string | null;
	/** Events open to this class. */
	events_count: number;
}

/** `GET /sport-classes/options?sport_id=` — one sport's classes, by `code`. */
export interface SportClassOption {
	sport_class_id: string;
	code: string;
	name: string | null;
}

/** The most sports `GET /sport-classes/stats` names — far above any games' para programme. */
export const SPORT_CLASS_STATS_SPORTS_MAX = 100;

/**
 * `GET /sport-classes/stats?games_id=` — the para classes of one games' sports,
 * and the count per sport (the Sport chip's counts), largest first.
 */
export const sportClassStatsQuery = z.object({ games_id: idField });
export type SportClassStatsQuery = z.input<typeof sportClassStatsQuery>;

export interface SportClassStats {
	/** Classes of this games' sports. */
	total: number;
	/** Sports with at least one class, by count descending, at most `SPORT_CLASS_STATS_SPORTS_MAX`. */
	by_sport: Array<{ sport_id: string; code: string; name: string; count: number }>;
}

// ─── Venues ──────────────────────────────────────────────────────────────────

export const VENUE_SORT_KEYS = ["code", "name", "city", "created_at"] as const;
export type VenueSortKey = (typeof VENUE_SORT_KEYS)[number];

export const listVenuesQuery = paginationQuery.extend({
	search: searchParam.optional(),
	/** A venue id for its courts / mats; `none` for the top-level venues only. */
	parent_venue_id: z.union([z.literal("none"), idField]).optional(),
	sort: z.enum(VENUE_SORT_KEYS).optional(),
	dir: dirParam.optional(),
});
export type ListVenuesQuery = z.input<typeof listVenuesQuery>;

export const CITY_MAX = 120;
const cityField = z.string().trim().min(1).max(CITY_MAX);

/**
 * `parent_venue_id` makes this a court / mat / pool inside that venue. One
 * level only: the parent must itself be top-level, and a venue that has
 * children cannot be given a parent (409 `{ field: "parent_venue_id" }`).
 */
export const createVenueBody = z.object({
	venue_id: clientIdField,
	code: competitionCodeField,
	name: referenceNameField,
	city: cityField.optional(),
	parent_venue_id: idField.optional(),
	/** M10: the transport cluster (drive times, dispatch). A venue with none is not offered as a transport place. */
	cluster_id: idField.nullable().optional(),
});
export type CreateVenueBody = z.input<typeof createVenueBody>;

/** `null` clears `city` or `cluster_id`, or makes the venue top-level again. */
export const updateVenueBody = z
	.object({
		code: competitionCodeField.optional(),
		name: referenceNameField.optional(),
		city: cityField.nullable().optional(),
		parent_venue_id: idField.nullable().optional(),
		cluster_id: idField.nullable().optional(),
		reason: reasonField.optional(),
	})
	.refine(changesSomething, NOTHING_TO_CHANGE);
export type UpdateVenueBody = z.input<typeof updateVenueBody>;

export interface VenueItem {
	venue_id: string;
	code: string;
	name: string;
	city: string | null;
	parent_venue_id: string | null;
	parent_code: string | null;
	parent_name: string | null;
	/** Courts / mats inside this venue. */
	children_count: number;
	/** Meets scheduled AT this venue — not at its children. */
	meets_count: number;
	/** M10: the transport cluster, or null (not a transport place yet). */
	cluster_id: string | null;
	cluster: ClusterRef | null;
	created_at: string;
	updated_at: string;
}

/**
 * `GET /venues/options` — every venue; with `games_id`, only the venues where a
 * top-level meet of that games is played (the meets list's `venue_id` filter
 * never comes up empty), plus the building of each such court so the picker
 * keeps its families. Venues belong to no games, so this is the only link.
 * A form that ASSIGNS a venue to a meet should list them all (no `games_id`).
 */
export const venueOptionsQuery = z.object({ games_id: idField.optional() });
export type VenueOptionsQuery = z.input<typeof venueOptionsQuery>;

/** `GET /venues/options` — each parent followed by its children. */
export interface VenueOption {
	venue_id: string;
	code: string;
	name: string;
	parent_venue_id: string | null;
}

/** `GET /venues/stats` — one statement, unfiltered (venues belong to no games). */
export interface VenueStats {
	total: number;
	top_level: number;
	/** Courts / mats / pools — venues with a parent. */
	courts: number;
	/** Top-level venues with at least one court. */
	with_courts: number;
}

// ─── GMS forms ───────────────────────────────────────────────────────────────
//
// The create / edit forms of the GMS settings pages, in the message-factory
// shape of `makeMainFunctionForm` (`../functions.ts`): English defaults here,
// translated messages from `apps/gms/src/lib/i18n/use-form-schemas.ts`.
//
// Every rule is built from the SAME builder as the wire field above (name,
// code, sort order, team size, medal count, entry quota, URL, class list), so
// a form cannot accept what the API refuses. What differs is only the INPUT
// shape a browser form has:
//
//   · optional text binds as `""` (never undefined) — the `to*Body` helpers
//     drop it on a create and turn it into `null` (clear) on an edit;
//   · a number `<input>` hands over a string — "" means "not set", so a
//     required number reports its own message and an optional one is omitted;
//   · a `<select>` of a vocabulary starts at `""` until something is picked;
//   · `name_translations` is one text box per locale, `""` = not translated.
//
// The `to*Body` edit helpers send only what changed, so the audit row's
// `changed_fields` names the edit (and the PATCH is never empty when the form
// changed something). `apps/api/tests/unit/contract-parity.reference-forms.test.ts`
// holds every factory to the API's verdict.

export interface ReferenceFormMessages {
	nameRequired: string;
	nameTooLong: string;
	codeRequired: string;
	/** Letters, digits, `-` and `_`; starts with a letter or a digit; 20 at most. */
	codeFormat: string;
	sortOrderInvalid: string;
	translationTooLong: string;
}

export const enReferenceFormMessages: ReferenceFormMessages = {
	nameRequired: "Enter a name",
	nameTooLong: `${REFERENCE_NAME_MAX} characters at most`,
	codeRequired: enCompetitionFieldMessages.codeRequired,
	codeFormat: enCompetitionFieldMessages.codeFormat,
	sortOrderInvalid: `Enter a whole number from 0 to ${SORT_ORDER_MAX}`,
	translationTooLong: `${REFERENCE_NAME_MAX} characters at most`,
};

/**
 * What a number `<input>` hands over: a string ("" when empty), or a number
 * with `valueAsNumber` (NaN when empty). Empty becomes `undefined`, so the
 * rule's own required / optional decides; "abc" becomes NaN and is refused
 * with the rule's message.
 */
const numberInput = z
	.union([z.string(), z.number()])
	.transform((v) => (typeof v === "number" ? (Number.isNaN(v) ? undefined : v) : v.trim() === "" ? undefined : Number(v)));
const formInt = <T extends z.ZodTypeAny>(rule: T) => numberInput.pipe(rule);

/** A vocabulary `<select>`: a string in (`""` until picked), one of the list out. */
const choice = <U extends string, T extends Readonly<[U, ...U[]]>>(values: T, message: string) =>
	z.string().pipe(z.enum(values, { errorMap: () => ({ message }) }));

/** One text box per translation locale; `""` = not translated. */
export type NameTranslationsForm = Record<TranslationLocale, string>;

const translationsForm = (tooLong: string) =>
	z.object(
		Object.fromEntries(TRANSLATION_LOCALES.map((code) => [code, z.string().trim().max(REFERENCE_NAME_MAX, tooLong)])) as Record<
			TranslationLocale,
			z.ZodString
		>,
	);

/** The translation boxes for a form: the stored ones, `""` for the rest. */
export const nameTranslationsFormValues = (stored?: NameTranslations | null): NameTranslationsForm =>
	Object.fromEntries(TRANSLATION_LOCALES.map((code) => [code, stored?.[code] ?? ""])) as NameTranslationsForm;

/** The boxes as the wire takes them: the empty ones left out (the object is stored whole). */
export const toNameTranslations = (f: NameTranslationsForm): NameTranslations =>
	Object.fromEntries(TRANSLATION_LOCALES.filter((code) => f[code] !== "").map((code) => [code, f[code]])) as NameTranslations;

const sameTranslations = (a: NameTranslations, b: NameTranslations): boolean => TRANSLATION_LOCALES.every((code) => (a[code] ?? "") === (b[code] ?? ""));

/** `""` → clear (`null`) if something was stored, else leave alone; an unchanged value is left alone too. */
const diffText = (next: string, prev: string | null): string | null | undefined =>
	next === "" ? (prev === null ? undefined : null) : next === prev ? undefined : next;

/** The same for an optional number: `undefined` in the form means "none". */
const diffNumber = (next: number | undefined, prev: number | null): number | null | undefined =>
	next === undefined ? (prev === null ? undefined : null) : next === prev ? undefined : next;

const nameFields = (m: ReferenceFormMessages) => ({
	code: makeCompetitionCodeField(m),
	name: nameRule(m.nameRequired, m.nameTooLong),
	name_translations: translationsForm(m.translationTooLong),
	sort_order: formInt(intRule(0, SORT_ORDER_MAX, m.sortOrderInvalid)),
	is_active: z.boolean(),
});

// ── Sport ───────────────────────────────────────────────────────────────────

export interface SportFormMessages extends ReferenceFormMessages {
	iconUrlInvalid: string;
}

export const enSportFormMessages: SportFormMessages = {
	...enReferenceFormMessages,
	iconUrlInvalid: "Enter a web address starting with http:// or https://",
};

/** `games_id` is not a field: it is the games the page is showing, and it is not editable. */
export function makeSportForm(m: SportFormMessages = enSportFormMessages) {
	return z.object({
		...nameFields(m),
		icon_url: z.literal("").or(httpUrlRule(m.iconUrlInvalid)),
	});
}
export const sportForm = makeSportForm();
/** What the form holds (`useForm<SportFormInput, unknown, SportForm>`). */
export type SportFormInput = z.input<typeof sportForm>;
/** What a valid form gives the `to*Body` helpers. */
export type SportForm = z.output<typeof sportForm>;

/** Create defaults, or the edit form's starting values. */
export const sportFormValues = (before?: SportItem): SportFormInput => ({
	code: before?.code ?? "",
	name: before?.name ?? "",
	name_translations: nameTranslationsFormValues(before?.name_translations),
	icon_url: before?.icon_url ?? "",
	sort_order: before?.sort_order ?? 0,
	is_active: before?.is_active ?? true,
});

export function toCreateSportBody(f: SportForm, gamesId: string): CreateSportBody {
	const translations = toNameTranslations(f.name_translations);
	return {
		games_id: gamesId,
		code: f.code,
		name: f.name,
		...(Object.keys(translations).length > 0 ? { name_translations: translations } : {}),
		...(f.icon_url !== "" ? { icon_url: f.icon_url } : {}),
		sort_order: f.sort_order,
		is_active: f.is_active,
	};
}

/** Only what changed. Empty (`{}`) = nothing to save: close the dialog instead of sending it. */
export function toUpdateSportBody(f: SportForm, before: SportItem): UpdateSportBody {
	const body: UpdateSportBody = {};
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	const translations = toNameTranslations(f.name_translations);
	if (!sameTranslations(translations, before.name_translations)) body.name_translations = translations;
	const icon = diffText(f.icon_url, before.icon_url);
	if (icon !== undefined) body.icon_url = icon;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	return body;
}

// ── Discipline ──────────────────────────────────────────────────────────────

export type DisciplineFormMessages = ReferenceFormMessages;
export const enDisciplineFormMessages: DisciplineFormMessages = enReferenceFormMessages;

/** `sport_id` is not a field: it is the sport whose page this is, and it is not editable. */
export function makeDisciplineForm(m: DisciplineFormMessages = enDisciplineFormMessages) {
	return z.object({ ...nameFields(m), side_scheme: z.enum(SIDE_SCHEMES) });
}
export const disciplineForm = makeDisciplineForm();
export type DisciplineFormInput = z.input<typeof disciplineForm>;
export type DisciplineForm = z.output<typeof disciplineForm>;

export const disciplineFormValues = (before?: DisciplineItem): DisciplineFormInput => ({
	code: before?.code ?? "",
	name: before?.name ?? "",
	name_translations: nameTranslationsFormValues(before?.name_translations),
	side_scheme: before?.side_scheme ?? "SEQUENCE",
	sort_order: before?.sort_order ?? 0,
	is_active: before?.is_active ?? true,
});

export function toCreateDisciplineBody(f: DisciplineForm, sportId: string): CreateDisciplineBody {
	const translations = toNameTranslations(f.name_translations);
	return {
		sport_id: sportId,
		code: f.code,
		name: f.name,
		...(Object.keys(translations).length > 0 ? { name_translations: translations } : {}),
		side_scheme: f.side_scheme,
		sort_order: f.sort_order,
		is_active: f.is_active,
	};
}

export function toUpdateDisciplineBody(f: DisciplineForm, before: DisciplineItem): UpdateDisciplineBody {
	const body: UpdateDisciplineBody = {};
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	const translations = toNameTranslations(f.name_translations);
	if (!sameTranslations(translations, before.name_translations)) body.name_translations = translations;
	if (f.side_scheme !== before.side_scheme) body.side_scheme = f.side_scheme;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	return body;
}

// ── Event ───────────────────────────────────────────────────────────────────

export interface EventFormMessages extends ReferenceFormMessages {
	disciplineRequired: string;
	genderRequired: string;
	competitorTypeRequired: string;
	resultFamilyRequired: string;
	resultTypeRequired: string;
	teamSizeInvalid: string;
	teamSizeTeamOnly: string;
	teamSizeMinOverMax: string;
	medalCountInvalid: string;
	maxEntriesInvalid: string;
	/** 0062: the shortlist quota and the qualifying standard. */
	maxShortlistInvalid: string;
	maxShortlistBelowEntries: string;
	qualifyingMarkInvalid: string;
	qualifyingMarkNoMeasure: string;
	qualifyingNoteTooLong: string;
	ceremonyInvalid: string;
	sportClassesTooMany: string;
	sportClassesDuplicate: string;
}

export const enEventFormMessages: EventFormMessages = {
	...enReferenceFormMessages,
	disciplineRequired: "Pick a discipline",
	genderRequired: "Pick who may enter",
	competitorTypeRequired: "Pick individual or team",
	resultFamilyRequired: "Pick how results are recorded",
	resultTypeRequired: "Pick what the result measures",
	teamSizeInvalid: `Enter a whole number from 1 to ${TEAM_SIZE_LIMIT}`,
	teamSizeTeamOnly: TEAM_ONLY,
	teamSizeMinOverMax: MIN_OVER_MAX,
	medalCountInvalid: `Enter a whole number from 0 to ${MEDAL_COUNT_MAX}`,
	maxEntriesInvalid: `Enter a whole number from 1 to ${MAX_ENTRIES_PER_NOC_LIMIT}`,
	maxShortlistInvalid: `Enter a whole number from 1 to ${MAX_SHORTLIST_PER_NOC_LIMIT}`,
	maxShortlistBelowEntries: SHORTLIST_BELOW_ENTRIES,
	qualifyingMarkInvalid: "Enter a mark the way the result is written (a time like 1:52.31, a distance like 7.85, a number)",
	qualifyingMarkNoMeasure: MARK_NEEDS_MEASURE,
	qualifyingNoteTooLong: `${QUALIFYING_NOTE_MAX} characters at most`,
	ceremonyInvalid: "Enter a date and time",
	sportClassesTooMany: `${EVENT_SPORT_CLASSES_MAX} classes at most`,
	sportClassesDuplicate: "List each class once",
};

/** What `<input type="datetime-local">` holds: `2027-12-05T19:30`, no zone. */
const LOCAL_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?$/;

/**
 * A stored instant as a `datetime-local` value, in the VIEWER's own time zone
 * (CLAUDE.md: every page shows the browser's zone). `null` → `""`.
 */
export function toLocalDateTimeInput(iso: string | null): string {
	if (iso === null) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const p = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * A `datetime-local` value (viewer's zone) as the instant the wire takes, `…Z`.
 * Something that is not a date is passed through unchanged, for the API to refuse
 * — never a `RangeError` out of a submit handler.
 */
export function fromLocalDateTimeInput(local: string): string {
	const d = new Date(local);
	return Number.isNaN(d.getTime()) ? local : d.toISOString();
}

/**
 * `discipline_id` IS a field here — the events page picks it — but only a
 * create sends it (an event does not move between disciplines).
 *
 * The team-size rules are the `events` CHECK constraints: sizes only on a TEAM
 * event, the maximum not below the minimum. When the page switches the type
 * away from TEAM it must clear both size boxes (`setValue(…, "")`), or the
 * hidden boxes keep the form from submitting.
 */
export function makeEventForm(m: EventFormMessages = enEventFormMessages) {
	return z
		.object({
			discipline_id: z.string().trim().uuid(m.disciplineRequired),
			...nameFields(m),
			gender: choice(EVENT_GENDERS, m.genderRequired),
			competitor_type: choice(COMPETITOR_TYPES, m.competitorTypeRequired),
			team_size_min: formInt(teamSizeRule(m.teamSizeInvalid).optional()),
			team_size_max: formInt(teamSizeRule(m.teamSizeInvalid).optional()),
			result_family: choice(RESULT_FAMILIES, m.resultFamilyRequired),
			result_type: choice(RESULT_TYPES, m.resultTypeRequired),
			lower_is_better: z.boolean(),
			is_medal_event: z.boolean(),
			gold_count: formInt(medalCountRule(m.medalCountInvalid)),
			silver_count: formInt(medalCountRule(m.medalCountInvalid)),
			bronze_count: formInt(medalCountRule(m.medalCountInvalid)),
			medal_ceremony_at: z
				.string()
				.trim()
				.refine((v) => v === "" || (LOCAL_DATE_TIME.test(v) && !Number.isNaN(new Date(v).getTime())), m.ceremonyInvalid),
			max_entries_per_noc: formInt(maxEntriesRule(m.maxEntriesInvalid).optional()),
			max_shortlist_per_noc: formInt(maxShortlistRule(m.maxShortlistInvalid).optional()),
			/** As typed, in the sport's notation (`parseMark` for the form's `result_type`); "" = no standard. */
			qualifying_mark: z.string().trim(),
			qualifying_note: z.string().trim().max(QUALIFYING_NOTE_MAX, m.qualifyingNoteTooLong),
			sport_class_ids: sportClassIdsRule(m.sportClassesTooMany, m.sportClassesDuplicate),
		})
		.superRefine((b, ctx) => {
			checkTeamSizes(b, ctx, m.teamSizeTeamOnly, m.teamSizeMinOverMax);
			checkQuotaAndStandard({ ...b, qualifying_mark: undefined }, ctx, m.maxShortlistBelowEntries, m.qualifyingMarkNoMeasure);
			if (b.qualifying_mark !== "") {
				const parsed = parseMark(b.result_type, b.qualifying_mark);
				if (!parsed.ok) {
					ctx.addIssue({ code: "custom", path: ["qualifying_mark"], message: parsed.reason === "no_measure" ? m.qualifyingMarkNoMeasure : m.qualifyingMarkInvalid });
				}
			}
		});
}
export const eventForm = makeEventForm();
export type EventFormInput = z.input<typeof eventForm>;
export type EventForm = z.output<typeof eventForm>;

/**
 * Create defaults (the table's own defaults: GENERIC / NONE, a medal event
 * awarding one of each), or the edit form's starting values. `disciplineId`
 * pre-fills a create from the page's discipline filter.
 */
export const eventFormValues = (before?: EventDetail, disciplineId = ""): EventFormInput => ({
	discipline_id: before?.discipline_id ?? disciplineId,
	code: before?.code ?? "",
	name: before?.name ?? "",
	name_translations: nameTranslationsFormValues(before?.name_translations),
	sort_order: before?.sort_order ?? 0,
	is_active: before?.is_active ?? true,
	gender: before?.gender ?? "",
	competitor_type: before?.competitor_type ?? "",
	team_size_min: before?.team_size_min ?? "",
	team_size_max: before?.team_size_max ?? "",
	result_family: before?.result_family ?? "GENERIC",
	result_type: before?.result_type ?? "NONE",
	lower_is_better: before?.lower_is_better ?? false,
	is_medal_event: before?.is_medal_event ?? true,
	gold_count: before?.gold_count ?? 1,
	silver_count: before?.silver_count ?? 1,
	bronze_count: before?.bronze_count ?? 1,
	medal_ceremony_at: toLocalDateTimeInput(before?.medal_ceremony_at ?? null),
	max_entries_per_noc: before?.max_entries_per_noc ?? "",
	max_shortlist_per_noc: before?.max_shortlist_per_noc ?? "",
	qualifying_mark: before && before.qualifying_mark !== null ? formatMark(before.result_type, before.qualifying_mark) : "",
	qualifying_note: before?.qualifying_note ?? "",
	sport_class_ids: before?.sport_classes.map((c) => c.sport_class_id) ?? [],
});

/** The form's qualifying mark in the canonical unit; null = none (the form already refused one that does not parse). */
const formMark = (f: EventForm): number | null => {
	if (f.qualifying_mark === "") return null;
	const parsed = parseMark(f.result_type, f.qualifying_mark);
	return parsed.ok ? parsed.value : null;
};

export function toCreateEventBody(f: EventForm): CreateEventBody {
	const translations = toNameTranslations(f.name_translations);
	return {
		discipline_id: f.discipline_id,
		code: f.code,
		name: f.name,
		...(Object.keys(translations).length > 0 ? { name_translations: translations } : {}),
		gender: f.gender,
		competitor_type: f.competitor_type,
		...(f.team_size_min !== undefined ? { team_size_min: f.team_size_min } : {}),
		...(f.team_size_max !== undefined ? { team_size_max: f.team_size_max } : {}),
		result_family: f.result_family,
		result_type: f.result_type,
		lower_is_better: f.lower_is_better,
		is_medal_event: f.is_medal_event,
		gold_count: f.gold_count,
		silver_count: f.silver_count,
		bronze_count: f.bronze_count,
		...(f.medal_ceremony_at !== "" ? { medal_ceremony_at: fromLocalDateTimeInput(f.medal_ceremony_at) } : {}),
		...(f.max_entries_per_noc !== undefined ? { max_entries_per_noc: f.max_entries_per_noc } : {}),
		...(f.max_shortlist_per_noc !== undefined ? { max_shortlist_per_noc: f.max_shortlist_per_noc } : {}),
		...(formMark(f) !== null ? { qualifying_mark: formMark(f) as number } : {}),
		...(f.qualifying_note !== "" ? { qualifying_note: f.qualifying_note } : {}),
		sort_order: f.sort_order,
		is_active: f.is_active,
		...(f.sport_class_ids.length > 0 ? { sport_class_ids: f.sport_class_ids } : {}),
	};
}

/**
 * Only what changed. The server still decides what a stored event allows
 * (competitors fix the type, results freeze the result settings, a LOCKED event
 * takes only name / translations / order / ceremony) — see `updateEventBody`.
 */
export function toUpdateEventBody(f: EventForm, before: EventDetail): UpdateEventBody {
	const body: UpdateEventBody = {};
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	const translations = toNameTranslations(f.name_translations);
	if (!sameTranslations(translations, before.name_translations)) body.name_translations = translations;
	if (f.gender !== before.gender) body.gender = f.gender;
	if (f.competitor_type !== before.competitor_type) body.competitor_type = f.competitor_type;
	const min = diffNumber(f.team_size_min, before.team_size_min);
	if (min !== undefined) body.team_size_min = min;
	const max = diffNumber(f.team_size_max, before.team_size_max);
	if (max !== undefined) body.team_size_max = max;
	if (f.result_family !== before.result_family) body.result_family = f.result_family;
	if (f.result_type !== before.result_type) body.result_type = f.result_type;
	if (f.lower_is_better !== before.lower_is_better) body.lower_is_better = f.lower_is_better;
	if (f.is_medal_event !== before.is_medal_event) body.is_medal_event = f.is_medal_event;
	if (f.gold_count !== before.gold_count) body.gold_count = f.gold_count;
	if (f.silver_count !== before.silver_count) body.silver_count = f.silver_count;
	if (f.bronze_count !== before.bronze_count) body.bronze_count = f.bronze_count;
	// Compared at the minute the input shows, so an untouched box never re-sends a stored time with seconds.
	if (f.medal_ceremony_at !== toLocalDateTimeInput(before.medal_ceremony_at)) {
		body.medal_ceremony_at = f.medal_ceremony_at === "" ? null : fromLocalDateTimeInput(f.medal_ceremony_at);
	}
	const shortlist = diffNumber(f.max_shortlist_per_noc, before.max_shortlist_per_noc);
	if (shortlist !== undefined) body.max_shortlist_per_noc = shortlist;
	// A changed result type changes the mark's unit: re-send the mark (the server clears it otherwise).
	const mark = formMark(f);
	if (mark !== before.qualifying_mark || (f.result_type !== before.result_type && mark !== null)) body.qualifying_mark = mark;
	const note = diffText(f.qualifying_note, before.qualifying_note);
	if (note !== undefined) body.qualifying_note = note;
	const entries = diffNumber(f.max_entries_per_noc, before.max_entries_per_noc);
	if (entries !== undefined) body.max_entries_per_noc = entries;
	if (f.sort_order !== before.sort_order) body.sort_order = f.sort_order;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	const stored = new Set(before.sport_classes.map((c) => c.sport_class_id));
	if (f.sport_class_ids.length !== stored.size || f.sport_class_ids.some((id) => !stored.has(id))) body.sport_class_ids = f.sport_class_ids;
	return body;
}

// ── Sport class ─────────────────────────────────────────────────────────────

export interface SportClassFormMessages {
	codeRequired: string;
	codeFormat: string;
	nameTooLong: string;
}

export const enSportClassFormMessages: SportClassFormMessages = {
	codeRequired: enReferenceFormMessages.codeRequired,
	codeFormat: enReferenceFormMessages.codeFormat,
	nameTooLong: enReferenceFormMessages.nameTooLong,
};

/** `name` is optional (S9 needs none); `sport_id` is the page's sport filter on a create and not editable. */
export function makeSportClassForm(m: SportClassFormMessages = enSportClassFormMessages) {
	return z.object({
		code: makeCompetitionCodeField(m),
		name: z.string().trim().max(REFERENCE_NAME_MAX, m.nameTooLong),
	});
}
export const sportClassForm = makeSportClassForm();
export type SportClassFormInput = z.input<typeof sportClassForm>;
export type SportClassForm = z.output<typeof sportClassForm>;

export const sportClassFormValues = (before?: SportClassItem): SportClassFormInput => ({
	code: before?.code ?? "",
	name: before?.name ?? "",
});

export const toCreateSportClassBody = (f: SportClassForm, sportId: string): CreateSportClassBody => ({
	sport_id: sportId,
	code: f.code,
	...(f.name !== "" ? { name: f.name } : {}),
});

export function toUpdateSportClassBody(f: SportClassForm, before: SportClassItem): UpdateSportClassBody {
	const body: UpdateSportClassBody = {};
	if (f.code !== before.code) body.code = f.code;
	const name = diffText(f.name, before.name);
	if (name !== undefined) body.name = name;
	return body;
}

// ── Venue ───────────────────────────────────────────────────────────────────

export interface VenueFormMessages {
	nameRequired: string;
	nameTooLong: string;
	codeRequired: string;
	codeFormat: string;
	cityTooLong: string;
	parentInvalid: string;
	/** M10. Optional so existing callers compile; defaults to the English copy. */
	clusterInvalid?: string;
}

export const enVenueFormMessages: VenueFormMessages = {
	nameRequired: enReferenceFormMessages.nameRequired,
	nameTooLong: enReferenceFormMessages.nameTooLong,
	codeRequired: enReferenceFormMessages.codeRequired,
	codeFormat: enReferenceFormMessages.codeFormat,
	cityTooLong: `${CITY_MAX} characters at most`,
	parentInvalid: "Pick a venue from the list",
};

/**
 * `parent_venue_id`: `""` = a top-level venue, else the venue this court /
 * mat / pool sits in. `cluster_id` (M10): `""` = none; ABSENT = this form
 * does not manage the cluster, and the update body leaves it alone.
 */
export function makeVenueForm(m: VenueFormMessages = enVenueFormMessages) {
	return z.object({
		code: makeCompetitionCodeField(m),
		name: nameRule(m.nameRequired, m.nameTooLong),
		city: z.string().trim().max(CITY_MAX, m.cityTooLong),
		parent_venue_id: z.literal("").or(z.string().trim().uuid(m.parentInvalid)),
		cluster_id: z
			.literal("")
			.or(z.string().trim().uuid(m.clusterInvalid ?? "Pick a cluster from the list"))
			.optional(),
	});
}
export const venueForm = makeVenueForm();
export type VenueFormInput = z.input<typeof venueForm>;
export type VenueForm = z.output<typeof venueForm>;

/** `parentVenueId` pre-fills a create made from inside a venue's courts list. */
export const venueFormValues = (before?: VenueItem, parentVenueId = ""): VenueFormInput => ({
	code: before?.code ?? "",
	name: before?.name ?? "",
	city: before?.city ?? "",
	parent_venue_id: before?.parent_venue_id ?? parentVenueId,
	cluster_id: before?.cluster_id ?? "",
});

export const toCreateVenueBody = (f: VenueForm): CreateVenueBody => ({
	code: f.code,
	name: f.name,
	...(f.city !== "" ? { city: f.city } : {}),
	...(f.parent_venue_id !== "" ? { parent_venue_id: f.parent_venue_id } : {}),
	...(f.cluster_id ? { cluster_id: f.cluster_id } : {}),
});

export function toUpdateVenueBody(f: VenueForm, before: VenueItem): UpdateVenueBody {
	const body: UpdateVenueBody = {};
	if (f.code !== before.code) body.code = f.code;
	if (f.name !== before.name) body.name = f.name;
	const city = diffText(f.city, before.city);
	if (city !== undefined) body.city = city;
	const parent = diffText(f.parent_venue_id, before.parent_venue_id);
	if (parent !== undefined) body.parent_venue_id = parent;
	if (f.cluster_id !== undefined) {
		const cluster = diffText(f.cluster_id, before.cluster_id);
		if (cluster !== undefined) body.cluster_id = cluster;
	}
	return body;
}
